import { COUNTRIES } from '@fruuit/shared/countries'
import {
  CONFIDENCE_LEVELS,
  type CoverageLabel,
  INVOLVEMENT_TYPES,
  RIGHTS_BASIS,
  SECTORS,
  SEVERITIES,
  THEMES,
  type Vocab,
} from '@fruuit/shared/vocab'
import type { BadgeTone } from '@fruuit/ui'

/**
 * Display helpers for coverage, dossier and dashboard screens. Vocabulary
 * labels come from `@fruuit/shared`, so the UI and the API never drift
 * (P-UI-008).
 */

function labelOf<T extends string>(v: Vocab<T>, value: string | null | undefined): string {
  if (!value) return ''
  return (v.labels as Record<string, string>)[value] ?? value
}

export const sectorLabel = (value: string | null | undefined) => labelOf(SECTORS, value)
export const countryLabel = (value: string | null | undefined) => labelOf(COUNTRIES, value)
export const themeLabel = (value: string | null | undefined) =>
  value ? labelOf(THEMES, value) : 'No theme recorded'
export const severityLabel = (value: string | null | undefined) => labelOf(SEVERITIES, value)
export const confidenceLabel = (value: string | null | undefined) =>
  labelOf(CONFIDENCE_LEVELS, value)
export const involvementLabel = (value: string | null | undefined) =>
  labelOf(INVOLVEMENT_TYPES, value)
export const rightsLabel = (value: string | null | undefined) => labelOf(RIGHTS_BASIS, value)

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
})

/** "22 Oct 2026"; dates are calendar days, shown in UTC. Missing → "Not recorded". */
export function formatDate(value: string | null | undefined, fallback = 'Not recorded'): string {
  if (!value) return fallback
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value)
  return Number.isNaN(date.getTime()) ? fallback : DATE.format(date)
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Not recorded'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Not recorded' : `${DATE_TIME.format(date)} UTC`
}

/** Derived coverage labels (STAT-010) map to the unified tones (P-UI-005). */
const LABEL_TONES: Record<CoverageLabel, BadgeTone> = {
  'Within recorded scope': 'positive',
  'Review overdue': 'attention',
  'Coverage gap: source check partial': 'watch',
  'Coverage gap: source check failed': 'attention',
  'Source review not completed': 'watch',
}

export function coverageLabelTone(label: string): BadgeTone {
  return LABEL_TONES[label as CoverageLabel] ?? 'quiet'
}

/** Only real http(s) links open in a new tab; reserved demo hosts render as text. */
export function isLinkable(url: string): boolean {
  try {
    const parsed = new URL(url)
    return (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      !parsed.hostname.endsWith('.invalid')
    )
  } catch {
    return false
  }
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

export function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}
